// src/pages/ExplorePage.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import exercisesData from "../data/exercises.json";
import { enhancedSearch, getSearchSuggestions, getTrendingTerms } from "../utils/enhancedSearch";
import { MUSCLE_GROUP_MAP, MUSCLE_GROUPS, getMappedMuscleGroups } from "../utils/muscleGroups";
import { generateSmartWorkout } from "../utils/workoutGeneration";
import PlaceholderImage from "../components/PlaceholderImage";
import { useImageHover } from "../hooks/useImageHover";
import { useLocalStorage } from "../hooks/useLocalStorage";

// Use centralized muscle groups
const muscleGroups = MUSCLE_GROUPS;
const muscleGroupMap = MUSCLE_GROUP_MAP;

export default function ExplorePage({ user }) {
  // Use custom hooks for localStorage state management
  const [search, setSearch] = useLocalStorage("exploreSearch", "", false);
  const [filterGroups, setFilterGroups] = useLocalStorage("explorefilterGroups", []);
  const [filterCategory, setFilterCategory] = useLocalStorage("exploreFilterCategory", "", false);
  const [filterLevel, setFilterLevel] = useLocalStorage("exploreFilterLevel", "", false);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Use the custom hook for image hover functionality
  const { hoveredId, setHoveredId, activeIndex, setActiveIndex } = useImageHover();

  // Muscle group dropdown state
  const [isMuscleDropdownOpen, setIsMuscleDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  
  // Enhanced search state
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [trendingTerms, setTrendingTerms] = useState([]);
  const muscleDropdownRef = useRef(null);
  const searchRef = useRef(null);


  // 🔹 Handle click outside search to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // 🔹 Enhanced search suggestions
  useEffect(() => {
    if (search.length >= 2) {
      const suggestions = getSearchSuggestions(search, 8);
      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [search]);

  // 🔹 Load trending terms
  useEffect(() => {
    setTrendingTerms(getTrendingTerms());
  }, []);

  // 🔹 Load exercises from local JSON (updated with correct hyphenation)
  useEffect(() => {
    const loadExercises = () => {
      try {
        setExercises(exercisesData);
      } catch (err) {
        console.error("Error loading exercises:", err);
      } finally {
        setLoading(false);
      }
    };
    loadExercises();
  }, []);


  // 🔹 Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        muscleDropdownRef.current &&
        !muscleDropdownRef.current.contains(e.target)
      ) {
        setIsMuscleDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔹 Apply filters with enhanced search
  const filteredResults = (() => {
    // First apply enhanced search if there's a search query
    let results = search.trim() 
      ? enhancedSearch(exercises, search)
      : exercises;

    // Then apply other filters
    return results.filter((ex) => {
      const mappedGroups = getMappedMuscleGroups(
        ex.primaryMuscles,
        ex.secondaryMuscles
      );

      const matchesGroup =
        filterGroups.length === 0 ||
        mappedGroups.some((g) =>
          filterGroups.some((fg) => g.toLowerCase() === fg.toLowerCase())
        );

      const matchesCategory =
        !filterCategory ||
        ex.category?.toLowerCase() === filterCategory.toLowerCase();
      const matchesLevel =
        !filterLevel || ex.level?.toLowerCase() === filterLevel.toLowerCase();

      return matchesGroup && matchesCategory && matchesLevel;
    });
  })();

  // Generate workout function
  const clearAllFilters = () => {
    setFilterGroups([]);
    setFilterCategory("");
    setFilterLevel("");
    setSearch("");
  };

  const generateWorkout = () => {
    const selectedGroups = filterGroups;
    const exerciseCount = 5;

    // Use smart workout generation with practical constraints
    const criteria = {
      muscleGroups: selectedGroups,
      category: filterCategory,
      level: filterLevel,
      equipment: null // Let the algorithm determine equipment compatibility
    };

    const workout = generateSmartWorkout(criteria, exerciseCount);

    if (workout.length === 0) {
      alert(
        "No exercises found matching your criteria. Try adjusting your filters."
      );
      return;
    }

    const today = new Date();
    const month = today.toLocaleString("default", { month: "long" });
    const day = today.getDate();
    const workoutName =
      selectedGroups.length > 0
        ? `${selectedGroups
            .map((group) => group.charAt(0).toUpperCase() + group.slice(1))
            .join(", ")} Workout on ${month} ${day}`
        : `Workout on ${month} ${day}`;

    localStorage.setItem("workout", JSON.stringify(workout));
    localStorage.setItem("workoutName", workoutName);
    localStorage.setItem("selectedGroups", JSON.stringify(selectedGroups));
    localStorage.setItem(
      "selectedCategory",
      JSON.stringify(filterCategory || null)
    );
    localStorage.setItem("selectedLevel", JSON.stringify(filterLevel || null));
    localStorage.setItem("fromLibrary", "false");
    localStorage.setItem("currentStep", "0");

    navigate("/preview");
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl text-center font-bold">Explore Exercises</h1>

      {/* Filters row */}
      <div className="flex flex-wrap gap-3">
        {/* Muscle group filter */}
        <div className="relative flex-1 min-w-0" ref={muscleDropdownRef}>
          <button
            onClick={() => setIsMuscleDropdownOpen((prev) => !prev)}
            className="border rounded px-4 py-3 w-full flex justify-between items-center bg-white h-16 sm:h-auto"
          >
            <span className="text-sm font-medium text-gray-700 leading-tight">
              {filterGroups.length > 0
                ? filterGroups
                    .map((fg) => fg.charAt(0).toUpperCase() + fg.slice(1))
                    .join(", ")
                : "All Muscle Groups"}
            </span>
            <svg
              className={`w-4 h-4 ml-3 transition-transform flex-shrink-0 ${
                isMuscleDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isMuscleDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
              {/* "All" option */}
              <label className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterGroups.length === 0}
                  onChange={() => setFilterGroups([])}
                  className="mr-2"
                />
                All Muscle Groups
              </label>

              {muscleGroups.map((group) => {
                const label = group.charAt(0).toUpperCase() + group.slice(1);
                return (
                  <label
                    key={group}
                    className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filterGroups.includes(group)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilterGroups([...filterGroups, group]);
                        } else {
                          setFilterGroups(
                            filterGroups.filter((g) => g !== group)
                          );
                        }
                      }}
                      className="mr-2"
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Category filter */}
        <div className="relative flex-1 min-w-0">
          <button
            onClick={() => setIsCategoryDropdownOpen((prev) => !prev)}
            className="border rounded px-4 py-3 w-full flex justify-between items-center bg-white h-16 sm:h-auto"
          >
            <span className="text-sm font-medium text-gray-700 leading-tight">
              {filterCategory
                ? filterCategory.charAt(0).toUpperCase() + filterCategory.slice(1)
                : <span className="hidden sm:inline">All Categories</span>}
              {!filterCategory && <span className="sm:hidden">All Cat-egories</span>}
            </span>
            <svg
              className={`w-4 h-4 ml-3 transition-transform flex-shrink-0 ${
                isCategoryDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isCategoryDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
              <button
                onClick={() => {
                  setFilterCategory("");
                  setIsCategoryDropdownOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-100"
              >
                <span className="hidden sm:inline">All Categories</span>
                <span className="sm:hidden">All Cat-egories</span>
              </button>
              {["strength", "cardio", "plyometrics", "stretching"].map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setFilterCategory(category);
                    setIsCategoryDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100"
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Level filter */}
        <div className="relative flex-1 min-w-0">
          <button
            onClick={() => setIsLevelDropdownOpen((prev) => !prev)}
            className="border rounded px-4 py-3 w-full flex justify-between items-center bg-white h-16 sm:h-auto"
          >
            <span className="text-sm font-medium text-gray-700 leading-tight">
              {filterLevel
                ? filterLevel.charAt(0).toUpperCase() + filterLevel.slice(1)
                : "All Levels"}
            </span>
            <svg
              className={`w-4 h-4 ml-3 transition-transform flex-shrink-0 ${
                isLevelDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isLevelDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
              <button
                onClick={() => {
                  setFilterLevel("");
                  setIsLevelDropdownOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-100"
              >
                All Levels
              </button>
              {["beginner", "intermediate", "expert"].map((level) => (
                <button
                  key={level}
                  onClick={() => {
                    setFilterLevel(level);
                    setIsLevelDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100"
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Clear All Filters Link */}
      {(filterGroups.length > 0 || filterCategory || filterLevel || search) && (
        <div className="flex justify-center mb-4">
          <button
            onClick={clearAllFilters}
            className="text-sm text-gray-600 hover:text-gray-800 underline transition-colors duration-200"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Action Buttons Row */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={generateWorkout}
          className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition duration-200 font-medium flex items-center justify-center gap-2"
        >
          <span>💪</span>
          Generate Workout
        </button>
        <button
          onClick={() => navigate('/custom-workout')}
          className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition duration-200 font-medium flex items-center justify-center gap-2"
        >
          <span>🛠️</span>
          Create Custom Workout
        </button>
      </div>

      {/* Search bar */}
      <div className="relative" ref={searchRef}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setShowSuggestions(search.length >= 2)}
          placeholder="Search for an exercise..."
          className="w-full border-2 rounded-lg px-4 py-2 border-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)] focus:outline-none"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-2 text-gray-500 hover:text-gray-800"
          >
            ✕
          </button>
        )}
        
        {/* Search Suggestions Dropdown */}
        {showSuggestions && searchSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            {searchSuggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => {
                  setSearch(suggestion);
                  setShowSuggestions(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
              >
                <span className="text-gray-700">{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Trending Search Terms */}
      {!search && trendingTerms.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Popular searches:</p>
          <div className="flex flex-wrap gap-2">
            {trendingTerms.slice(0, 8).map((term, index) => (
              <button
                key={index}
                onClick={() => setSearch(term)}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors"
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results count */}
      {!loading && (
        <p className="text-sm text-gray-600">
          {(() => {
            const parts = [];
            if (search) parts.push(`"${search}"`);
            if (filterGroups.length > 0) {
              parts.push(
                filterGroups
                  .map((fg) => fg.charAt(0).toUpperCase() + fg.slice(1))
                  .join(", ")
              );
            }
            if (filterCategory)
              parts.push(
                filterCategory.charAt(0).toUpperCase() +
                  filterCategory.slice(1)
              );
            if (filterLevel)
              parts.push(
                filterLevel.charAt(0).toUpperCase() + filterLevel.slice(1)
              );

            const filterDescription =
              parts.length > 0 ? ` for ${parts.join(" ")}` : "";
            return `Showing ${filteredResults.length} ${
              filteredResults.length === 1 ? "exercise" : "exercises"
            }${filterDescription}`;
          })()}
        </p>
      )}

      {/* Results list */}
      <div className="space-y-3">
        {loading ? (
          <p>Loading exercises...</p>
        ) : filteredResults.length > 0 ? (
          filteredResults.map((ex) => {
            const mappedGroups = getMappedMuscleGroups(
              ex.primaryMuscles,
              ex.secondaryMuscles
            );
            const categoryMatch =
              filterCategory &&
              ex.category?.toLowerCase() === filterCategory.toLowerCase();
            const levelMatch =
              filterLevel && ex.level?.toLowerCase() === filterLevel.toLowerCase();

            return (
              <Link
                key={ex.id}
                to={`/exercise/${ex.id}`}
                className="block group"
              >
                <div
                  className="p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] border border-gray-100 hover:border-green-200 cursor-pointer flex items-start justify-between"
                  onMouseEnter={() => setHoveredId(ex.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Left: text */}
                  <div className="flex-1 pr-4">
                    <h2 className="font-semibold">{ex.name}</h2>
                    <p className="text-sm">
                      <span
                        className={
                          categoryMatch
                            ? "text-green-600 font-medium"
                            : "text-gray-600"
                        }
                      >
                        {ex.category}
                      </span>{" "}
                      |{" "}
                      <span
                        className={
                          levelMatch
                            ? "text-green-600 font-medium"
                            : "text-gray-600"
                        }
                      >
                        {ex.level}
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {mappedGroups.map((group) => {
                        const isMatch =
                          filterGroups.length > 0 &&
                          filterGroups.some(
                            (fg) => fg.toLowerCase() === group.toLowerCase()
                          );
                        return (
                          <span
                            key={group}
                            className={`text-xs px-2 py-1 rounded-full ${
                              isMatch
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-200 text-gray-700"
                            }`}
                          >
                            {group.charAt(0).toUpperCase() + group.slice(1)}
                          </span>
                        );
                      })}
                    </div>
                    {/* Click indicator text */}
                    <div className="flex items-center gap-1 mt-2 text-xs text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <span>View details</span>
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Right: crossfading image with click indicator */}
                  <div className="relative w-24 h-24 shrink-0">
                    {ex.images?.slice(0, 2).map((img, i) => (
                      img === "placeholder.jpg" ? (
                        <PlaceholderImage 
                          key={i}
                          className={`absolute inset-0 w-full h-full rounded border ${
                            (hoveredId === ex.id && activeIndex === i) ||
                            (hoveredId !== ex.id && i === 0)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      ) : (
                        <img
                          key={i}
                          src={`/exercise-images/${img}`}
                          alt={`${ex.name} ${i + 1}`}
                          className={`absolute inset-0 w-full h-full object-cover rounded border transition-opacity duration-700 ${
                            (hoveredId === ex.id && activeIndex === i) ||
                            (hoveredId !== ex.id && i === 0)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      )
                    ))}
                    {/* Click indicator overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded border flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <svg
                          className="w-6 h-6 text-white drop-shadow-lg"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <p className="text-gray-500">No results found.</p>
        )}
      </div>
    </div>
  );
}
